import { AsyncPipe, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { Observable, combineLatest, pairwise } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { AccountFacade } from 'ish-core/facades/account.facade';
import { HttpError } from 'ish-core/models/http-error/http-error.model';
import { MfaChallenge } from 'ish-core/models/mfa-challenge/mfa-challenge.model';
import { MfaDeviceTrustService } from 'ish-core/services/mfa-device-trust/mfa-device-trust.service';
import { whenTruthy } from 'ish-core/utils/operators';
import { ErrorMessageComponent } from 'ish-shared/components/common/error-message/error-message.component';
import { MfaOtpInputComponent } from 'ish-shared/components/login/mfa-otp-input/mfa-otp-input.component';

function otpLengthValidator(length: number): ValidatorFn {
  return (control): ValidationErrors | null => {
    const value: string = control.value ?? '';
    if (!value) {
      return null;
    }
    return /^\d+$/.test(value) && value.length === length ? null : { otpLength: true };
  };
}

/**
 * Presidio MFA step — trust device 90 days, 15-minute validity copy, no resend countdown.
 */
@Component({
  selector: 'ish-mfa-verification-form',
  templateUrl: './mfa-verification-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AsyncPipe, NgIf, ReactiveFormsModule, TranslatePipe, MfaOtpInputComponent, ErrorMessageComponent],
})
export class MfaVerificationFormComponent {
  private accountFacade = inject(AccountFacade);
  private deviceTrust = inject(MfaDeviceTrustService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  @ViewChild(MfaOtpInputComponent) private otpInput?: MfaOtpInputComponent;

  mfaChallenge$: Observable<MfaChallenge> = this.accountFacade.mfaChallenge$;
  mfaVerifying$: Observable<boolean> = this.accountFacade.mfaVerifying$;
  mfaResending$: Observable<boolean> = this.accountFacade.mfaResending$;
  mfaError$: Observable<HttpError> = this.accountFacade.mfaError$;

  otpControl = new FormControl('', { nonNullable: true });
  form = new FormGroup({ otp: this.otpControl });
  otpLength = 6;
  trustDevice = false;

  constructor() {
    this.mfaChallenge$.pipe(whenTruthy(), take(1), takeUntilDestroyed(this.destroyRef)).subscribe(challenge => {
      this.otpLength = challenge.otpLength;
      this.otpControl.setValidators([Validators.required, otpLengthValidator(challenge.otpLength)]);
      this.otpControl.updateValueAndValidity();
      setTimeout(() => this.otpInput?.focusFirst());
    });

    combineLatest([this.mfaResending$, this.mfaError$])
      .pipe(pairwise(), takeUntilDestroyed(this.destroyRef))
      .subscribe(([[wasResending], [isResending, error]]) => {
        if (wasResending && !isResending && !error) {
          this.otpControl.reset('');
          this.cdr.markForCheck();
        }
      });

    this.mfaError$.pipe(pairwise(), takeUntilDestroyed(this.destroyRef)).subscribe(([previous, current]) => {
      if (!previous && current) {
        this.otpInput?.focusFirst();
      }
    });

    this.accountFacade.isLoggedIn$
      .pipe(filter(Boolean), take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.deviceTrust.commitPendingTrust());
  }

  onTrustChange(event: Event): void {
    this.trustDevice = !!(event.target as HTMLInputElement)?.checked;
  }

  verify() {
    if (this.otpControl.valid) {
      if (this.trustDevice) {
        this.deviceTrust.requestTrustOnSuccess();
      } else {
        this.deviceTrust.clearTrustRequest();
      }
      this.accountFacade.verifyMfaOtp(this.otpControl.value);
    }
  }

  resend() {
    this.accountFacade.resendMfaOtp();
  }

  backToLogin() {
    this.deviceTrust.clearTrustRequest();
    this.accountFacade.clearMfaChallenge();
  }
}
